package com.eladfinish.bible.contest.screens.browse.usecases

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.constants.StringConstants.Regex.OTIOT_1_TO_3
import com.eladfinish.bible.contest.constants.strings.traditionNameAfterLabel
import com.eladfinish.bible.contest.data.ChumashItem
import com.eladfinish.bible.contest.data.ParashaItem
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.data.common.stringResolver.StringResolver
import com.eladfinish.bible.contest.domain.catalog.BibleCatalog
import com.eladfinish.bible.contest.extensions.convertToDataClass
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.extensions.splitByRegex
import com.eladfinish.bible.contest.goalenu
import com.eladfinish.bible.contest.haftarah
import com.eladfinish.bible.contest.parashot
import com.eladfinish.bible.contest.parashot_json
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.VerseReferenceCV
import com.eladfinish.bible.contest.sefer
import com.eladfinish.bible.contest.tradition
import com.eladfinish.bible.contest.utils.GetParashotUseCase.Companion.validateBook
import com.eladfinish.bible.contest.utils.MyStringUtils.replaceSpaceWithNoBreakSpace
import com.eladfinish.bible.contest.utils.gematriaWord
import com.eladfinish.bible.contest.utils.removeWhitespacesAndParshiot
import dev.icerock.moko.resources.desc.desc

class HaftarotUseCase(
    private val assetReader: AssetReader,
    private val stringResolver: StringResolver,
    private val getContentUseCase: GetContentUseCase,
) {

    fun parseHaftarot(
        parashaName: String,
        bookIndex: Int,
        eachVerseInNewLine: Boolean,
        onlyQri: Boolean,
    ): StringBuilder {

        validateBook(book = bookIndex)

        // this marker is LOCALIZED — the "בחר עלייה" scroll needle must be built from the same
        // string resource; keep in sync with aliyahMarkerLabel (AliyotParasha.kt)
        val haftara = "[" + stringResolver.resolveString(SharedRes.strings.haftarah.desc()) + "]"
        val sb = StringBuilder(haftara)
        val data = assetReader.readFile(SharedRes.assets.parashot.parashot_json)

        val chumashim = data.convertToDataClass<List<ChumashItem>>()

        val (_, _, parashot) = chumashim[bookIndex - 1]

        val effectiveParashaName = resolveParashaNameForHaftara(
            requestedParashaName = parashaName,
            parashot = parashot,
        )

        for (parasha in parashot) {
            val name = normalizeParashaName(parasha.name)

            if (normalizeParashaName(effectiveParashaName) == name) {
                val haftaraItem = parasha.haftara

                for (tradition in haftaraItem.keys) {

                    sb.append("\n")
                        .append(stringResolver.resolveString(SharedRes.strings.tradition.desc()))
                        .append(" ")
                        .append(traditionNameAfterLabel(tradition))

                    sb.append("\n")

                    val sources = parasha.haftaraSources(tradition)

                    for (source in sources!!) {
                        var content = ""

                        // Split by space that is not followed by apostrophe, e.g. Kings A'| ...
                        val bookAndChapters: List<String> = source.splitByRegex("\\s(?!.')")
                        val book = bookAndChapters[0]

                        sb.append("[")
                            .append(stringResolver.resolveString(SharedRes.strings.sefer.desc()))
                            .append(" ")
                            .append(book)
                            .append("]")
                            .append("\n")

                        val chapters = bookAndChapters[1]
                        val hebBookName = book.replaceRegex(regex = "'", replacement = "")

                        val (haftaraBookIndex, engBookName) = BibleCatalog.books.withIndex()
                            .first { it.value.hebrew == hebBookName }
                            .let {
                                it.index.plus(1) to it.value.trans
                            }

                        val regex = "$OTIOT_1_TO_3,$OTIOT_1_TO_3-?$OTIOT_1_TO_3?,?$OTIOT_1_TO_3?".toRegex()
                        regex.find(chapters)?.let {
                            val startChapter = it.groups[1]?.value.gematriaWord()
                            val startVerse = it.groups[2]?.value.gematriaWord()

                            // A,B
                            var endChapter = startChapter
                            var endVerse = startVerse

                            if (it.groups[3]?.value != null) {
                                if (it.groups[4]?.value == null) {
                                    // A,B-C
                                    endVerse = it.groups[3]?.value.gematriaWord()
                                } else {
                                    // A,B-C,D
                                    endChapter = it.groups[3]?.value.gematriaWord()
                                    endVerse = it.groups[4]?.value.gematriaWord()
                                }
                            }

                            for (currentChapter in startChapter..endChapter) {
                                content += getContentUseCase.getContent(
                                    bookIndex = haftaraBookIndex,
                                    bookName = engBookName,
                                    currentChapter = currentChapter,
                                    startVerseReferenceCV = VerseReferenceCV(chapter = startChapter, verse = startVerse),
                                    endVerseReferenceCV = VerseReferenceCV(chapter = endChapter, verse = endVerse),
                                    onlyQri = onlyQri,
                                ) + "\n"

                                content = content.removeWhitespacesAndParshiot()
                                content = content.replaceSpaceWithNoBreakSpace()
                                content += " "

                                if (eachVerseInNewLine) {
                                    content = content.replaceRegex(regex = "׃ ?", replacement = "׃\n").trim()
                                }
                            }
                        }

                        sb.append(content)
                        sb.append("\n")
                    }
                }

                return sb.deleteAt(sb.length - 1) // delete last "\n"
                    .append("\n\n")
                    .append(stringResolver.resolveString(SharedRes.strings.goalenu.desc()))
            }
        }

        return StringBuilder("שגיאה")
    }

    private fun normalizeParashaName(input: String): String =
        input
            .trim()
            .replace("\u00A0", " ") // NBSP -> space
            .replace(Regex("\\s+"), " ")

    private fun splitCombinedParashaName(input: String): List<String> {
        // Supports: "-", "–", "—", "־" (Hebrew maqaf)
        return normalizeParashaName(input)
            .split(Regex("\\s*[\\-–—־]\\s*"))
            .map { it.trim() }
            .filter { it.isNotEmpty() }
    }

    private fun resolveParashaNameForHaftara(
        requestedParashaName: String,
        parashot: List<ParashaItem>,
    ): String {
        val requested = normalizeParashaName(requestedParashaName)

        // 1) Exact match
        parashot.firstOrNull { normalizeParashaName(it.name) == requested }?.let {
            return normalizeParashaName(it.name)
        }

        // 2) Combined parasha: prefer the second one (e.g. "בהר - בחוקותי" -> "בחוקותי")
        val parts = splitCombinedParashaName(requested)
        if (parts.size >= 2) {
            val second = normalizeParashaName(parts.last())

            parashot.firstOrNull { normalizeParashaName(it.name) == second }?.let {
                return normalizeParashaName(it.name)
            }

            // If both parts exist in this chumash list, prefer pair==2
            val candidates = parashot.filter { p ->
                val n = normalizeParashaName(p.name)
                parts.any { normalizeParashaName(it) == n }
            }
            candidates.firstOrNull { it.pair == 2 }?.let { return normalizeParashaName(it.name) }
            candidates.lastOrNull()?.let { return normalizeParashaName(it.name) }
        }

        // 3) Fallback: contains-based match (for weird formatting)
        val containsCandidates = parashot.filter { p ->
            requested.contains(normalizeParashaName(p.name))
        }
        containsCandidates.firstOrNull { it.pair == 2 }?.let { return normalizeParashaName(it.name) }
        containsCandidates.firstOrNull()?.let { return normalizeParashaName(it.name) }

        // give up - original behavior will return "שגיאה"
        return requested
    }
}
